import { Star, X } from 'lucide-react';

import { Badge } from '@/modules/ui/components/badge/Badge';
import { ConfirmDeleteDialog } from '@/modules/ui/components/confirm-delete-dialog/ConfirmDeleteDialog';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/modules/ui/components/tooltip/Tooltip';
import type { UserApi } from '@/modules/users/types';

export interface TenantBadgeProps {
  tenantId: string;
  user: UserApi;
  companyName: string;
  isDefault: boolean;
  onSetDefault: (userId: string, tenantId: string) => void;
  onRemove: (userId: string, tenantId: string) => void;
}

export const TenantBadge = ({
  tenantId,
  user,
  companyName,
  isDefault,
  onSetDefault,
  onRemove,
}: TenantBadgeProps) => {
  return (
    <Badge variant={isDefault ? 'default' : 'secondary'} className="gap-1 pr-1">
      {companyName}
      <div className="flex items-center gap-0.5">
        {isDefault ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="p-0.5">
                <Star
                  size={12}
                  fill="currentColor"
                  className="text-primary-foreground"
                />
              </div>
            </TooltipTrigger>
            <TooltipContent variant="primary">
              <p>Current default tenant</p>
            </TooltipContent>
          </Tooltip>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                role="button"
                tabIndex={0}
                onClick={() => onSetDefault(user.id, tenantId)}
                className="hover:bg-primary/20 hover:text-primary rounded-full p-0.5 transition-colors"
              >
                <Star size={12} className="cursor-pointer" />
              </div>
            </TooltipTrigger>
            <TooltipContent variant="primary">
              <p>Set as default</p>
            </TooltipContent>
          </Tooltip>
        )}

        <ConfirmDeleteDialog
          title="Remove Tenant"
          description={`Are you sure you want to remove ${companyName} from this user?`}
          onDelete={() => onRemove(user.id, tenantId)}
          trigger={
            <div
              role="button"
              tabIndex={0}
              className="hover:bg-destructive/20 hover:text-destructive rounded-full p-0.5 transition-colors"
            >
              <X size={12} className="cursor-pointer" />
            </div>
          }
        />
      </div>
    </Badge>
  );
};
